import type { LocalizedString } from "../localization";
import { hasLocalizedStringValue } from "../localization";

export type EventFieldSuggestions = {
  name: string;
  title: LocalizedString;
  body: LocalizedString;
  logo: string;
};

export type SpaceSuggestionSource = {
  name?: string | null;
  title?: LocalizedString | null;
  body?: LocalizedString | null;
  banner?: string | null;
};

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

/** Local calendar date as `yyyy-MM-dd`. */
export function formatLocalDateYmd(date: Date): string {
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
}

export function formatEventInternalName(
  spaceName: string,
  date: Date = new Date()
): string {
  const trimmed = spaceName.trim();
  if (!trimmed) {
    return formatLocalDateYmd(date);
  }
  return `${trimmed} · ${formatLocalDateYmd(date)}`;
}

export function buildEventSuggestionsFromSpace(
  space: SpaceSuggestionSource,
  date: Date = new Date()
): EventFieldSuggestions {
  const spaceName = typeof space.name === "string" ? space.name.trim() : "";
  const banner = typeof space.banner === "string" ? space.banner.trim() : "";
  return {
    name: formatEventInternalName(spaceName, date),
    title: { ...(space.title ?? {}) },
    body: { ...(space.body ?? {}) },
    logo: banner,
  };
}

function isEmptyString(value: unknown): boolean {
  return value == null || (typeof value === "string" && value.trim() === "");
}

function localizedEqual(
  a: LocalizedString | undefined | null,
  b: LocalizedString | undefined | null
): boolean {
  const left = a ?? {};
  const right = b ?? {};
  const keys = new Set([...Object.keys(left), ...Object.keys(right)]);
  for (const key of keys) {
    const lv = left[key];
    const rv = right[key];
    const ls = typeof lv === "string" ? lv : lv == null ? "" : String(lv);
    const rs = typeof rv === "string" ? rv : rv == null ? "" : String(rv);
    if (ls !== rs) {
      return false;
    }
  }
  return true;
}

/** Apply when empty or still equal to the last auto-suggestion. */
export function shouldApplySuggestion(
  current: unknown,
  previousSuggested: unknown
): boolean {
  if (typeof current === "string" || current == null) {
    if (isEmptyString(current)) {
      return true;
    }
    return (
      typeof previousSuggested === "string" &&
      current.trim() === previousSuggested.trim()
    );
  }

  if (typeof current === "object") {
    const localized = current as LocalizedString;
    if (!hasLocalizedStringValue(localized)) {
      return true;
    }
    if (
      previousSuggested == null ||
      typeof previousSuggested !== "object"
    ) {
      return false;
    }
    return localizedEqual(
      localized,
      previousSuggested as LocalizedString
    );
  }

  return false;
}
